// Landing page shown when the Mini App URL is visited in a regular browser
// (no Telegram initData). A marketing page that routes users to the bot AND
// shows the full catalog of what they can actually do inside.

interface ServiceRow { name: string; desc: string; price: string }

interface Copy {
  badge: string; title: string; subtitle: string; body: string;
  feature1: string; feature2: string; feature3: string; feature4: string;
  servicesHeader: string; freeRow: string;
  services: ServiceRow[];
  subsHeader: string;
  subPro: ServiceRow; subPremium: ServiceRow;
  cta: string; secondary: string; noSignup: string;
  langsLabel: string;
}

const COPY: Record<string, Copy> = {
  pl: {
    badge: 'Codzienny tarot w Telegramie',
    title: 'Twoja karta dnia',
    subtitle: 'Rider-Waite. 9 języków. Bez AI w runtime, bez reklam.',
    body: 'Każdego ranka o 9:00 dostajesz jedną kartę. Ta sama karta przez cały dzień — deterministyczna, nie da się „przelosować”. Pod nią — pełny katalog usług, jeśli chcesz pójść głębiej.',
    feature1: '78 kart Rider-Waite · public domain',
    feature2: 'Codzienny push o 9:00 · 9 języków',
    feature3: 'Płatności Telegram Stars · bez subskrypcji jeśli nie chcesz',
    feature4: 'Bez konta, bez e-maila, bez tracking cookies',
    servicesHeader: 'Co możesz robić',
    freeRow: 'Codzienna karta — gratis, na zawsze',
    services: [
      { name: 'Dodatkowa karta', desc: 'Drugi ruch tego samego dnia', price: '30 ⭐' },
      { name: 'Pytanie tak/nie', desc: 'Jedna karta, jasna odpowiedź', price: '39 ⭐' },
      { name: 'Pogłęb dzisiejszą kartę', desc: 'Miłość · praca · finanse · rada', price: '69 ⭐' },
      { name: 'Rozkład 3 kart', desc: 'Przeszłość · teraz · przyszłość', price: '99 ⭐' },
      { name: 'Rozkład miłosny', desc: 'Ty · druga osoba · wspólna droga', price: '129 ⭐' },
      { name: 'Rozkład zawodowy', desc: 'Sytuacja · wyzwanie · wynik', price: '129 ⭐' },
      { name: 'Odblokuj odwrócone znaczenia', desc: 'Jednorazowo, na zawsze', price: '149 ⭐' },
      { name: 'Podkowa (5 kart)', desc: 'Przeszłość · teraz · ukryte · rada · wynik', price: '179 ⭐' },
      { name: 'Prognoza miesięczna', desc: '4 karty — jedna na każdy tydzień', price: '249 ⭐' },
      { name: 'Krzyż celtycki', desc: 'Pełny rozkład 10-kartowy', price: '299 ⭐' },
      { name: 'Koło roku', desc: '12 kart — jedna na każdy miesiąc', price: '599 ⭐' },
    ],
    subsHeader: 'Subskrypcje',
    subPro: { name: 'Pro', desc: 'Bez limitów: codzienne, 3-kart, pogłębienia, tak/nie', price: '199 ⭐ /mies.' },
    subPremium: { name: 'Premium', desc: 'Wszystko Pro + krzyż celtycki + miesięczna prognoza', price: '499 ⭐ /mies.' },
    cta: 'Otwórz w Telegramie',
    secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'Bez rejestracji · Bez e-maila · Bez tracking cookies',
    langsLabel: 'Języki',
  },
  en: {
    badge: 'Daily tarot inside Telegram',
    title: 'Your card of the day',
    subtitle: 'Rider-Waite. 9 languages. No runtime AI, no ads.',
    body: 'Every morning at 9:00 you get one card. The same card all day — deterministic, no re-rolls. Below it — the full catalog of what you can do, if you want to go deeper.',
    feature1: '78 Rider-Waite cards · public domain',
    feature2: 'Daily push at 9:00 · 9 languages',
    feature3: 'Telegram Stars payments · no forced subscription',
    feature4: 'No account, no email, no tracking cookies',
    servicesHeader: 'What you can do',
    freeRow: 'Daily card — free, forever',
    services: [
      { name: 'Extra card', desc: 'A second move on the same day', price: '30 ⭐' },
      { name: 'Yes/No question', desc: 'One card, clear answer', price: '39 ⭐' },
      { name: 'Deepen today\'s card', desc: 'Love · career · money · advice', price: '69 ⭐' },
      { name: '3-card spread', desc: 'Past · present · future', price: '99 ⭐' },
      { name: 'Love spread', desc: 'You · the other · the path between', price: '129 ⭐' },
      { name: 'Career spread', desc: 'Situation · challenge · outcome', price: '129 ⭐' },
      { name: 'Unlock reversed meanings', desc: 'One-time, forever', price: '149 ⭐' },
      { name: 'Horseshoe (5 cards)', desc: 'Past · now · hidden · advice · outcome', price: '179 ⭐' },
      { name: 'Monthly forecast', desc: '4 cards — one per week', price: '249 ⭐' },
      { name: 'Celtic cross', desc: 'Full 10-card spread', price: '299 ⭐' },
      { name: 'Wheel of the year', desc: '12 cards — one per month', price: '599 ⭐' },
    ],
    subsHeader: 'Subscriptions',
    subPro: { name: 'Pro', desc: 'Unlimited: daily, 3-card, deepenings, yes/no', price: '199 ⭐ /mo' },
    subPremium: { name: 'Premium', desc: 'Everything Pro + Celtic cross + monthly forecast', price: '499 ⭐ /mo' },
    cta: 'Open in Telegram', secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'No signup · No email · No tracking cookies',
    langsLabel: 'Languages',
  },
  uk: {
    badge: 'Щоденне таро в Telegram', title: 'Ваша карта дня',
    subtitle: 'Rider-Waite. 9 мов. Без AI в runtime, без реклами.',
    body: 'Щоранку о 9:00 ви отримуєте одну карту. Та сама карта на весь день — детермінована, без можливості перекинути. Нижче — повний каталог послуг, якщо хочете глибше.',
    feature1: '78 карт Rider-Waite · public domain',
    feature2: 'Щоденна розсилка о 9:00 · 9 мов',
    feature3: 'Оплата Telegram Stars · без підписки, якщо не хочете',
    feature4: 'Без акаунту, без email, без трекінгу',
    servicesHeader: 'Що ви можете робити',
    freeRow: 'Щоденна карта — безкоштовно, назавжди',
    services: [
      { name: 'Додаткова карта', desc: 'Другий хід того самого дня', price: '30 ⭐' },
      { name: 'Питання так/ні', desc: 'Одна карта — чітка відповідь', price: '39 ⭐' },
      { name: 'Поглибити карту дня', desc: 'Кохання · робота · гроші · порада', price: '69 ⭐' },
      { name: 'Розклад на 3 карти', desc: 'Минуле · теперішнє · майбутнє', price: '99 ⭐' },
      { name: 'Любовний розклад', desc: 'Ви · інша людина · спільний шлях', price: '129 ⭐' },
      { name: 'Кар\'єрний розклад', desc: 'Ситуація · виклик · результат', price: '129 ⭐' },
      { name: 'Розблокувати перевернуті', desc: 'Одноразово, назавжди', price: '149 ⭐' },
      { name: 'Підкова (5 карт)', desc: 'Минуле · зараз · приховане · порада · результат', price: '179 ⭐' },
      { name: 'Місячний прогноз', desc: '4 карти — по одній на тиждень', price: '249 ⭐' },
      { name: 'Кельтський хрест', desc: 'Повний розклад на 10 карт', price: '299 ⭐' },
      { name: 'Колесо року', desc: '12 карт — по одній на місяць', price: '599 ⭐' },
    ],
    subsHeader: 'Підписки',
    subPro: { name: 'Pro', desc: 'Без лімітів: щоденне, 3-карти, поглиблення, так/ні', price: '199 ⭐ /міс.' },
    subPremium: { name: 'Premium', desc: 'Все Pro + кельтський хрест + місячний прогноз', price: '499 ⭐ /міс.' },
    cta: 'Відкрити в Telegram', secondary: 'Бот · @thisisthedailytarotbot',
    noSignup: 'Без реєстрації · Без email · Без трекінгу',
    langsLabel: 'Мови',
  },
  ru: {
    badge: 'Ежедневное таро в Telegram', title: 'Ваша карта дня',
    subtitle: 'Rider-Waite. 9 языков. Без AI в runtime, без рекламы.',
    body: 'Каждое утро в 9:00 вы получаете одну карту. Та же карта весь день — детерминированная, без перетасовок. Ниже — полный каталог услуг, если хотите глубже.',
    feature1: '78 карт Rider-Waite · public domain',
    feature2: 'Ежедневная рассылка в 9:00 · 9 языков',
    feature3: 'Оплата Telegram Stars · без обязательной подписки',
    feature4: 'Без аккаунта, без email, без трекинга',
    servicesHeader: 'Что вы можете делать',
    freeRow: 'Ежедневная карта — бесплатно, навсегда',
    services: [
      { name: 'Дополнительная карта', desc: 'Второй ход того же дня', price: '30 ⭐' },
      { name: 'Вопрос да/нет', desc: 'Одна карта — чёткий ответ', price: '39 ⭐' },
      { name: 'Углубить карту дня', desc: 'Любовь · работа · деньги · совет', price: '69 ⭐' },
      { name: 'Расклад на 3 карты', desc: 'Прошлое · настоящее · будущее', price: '99 ⭐' },
      { name: 'Любовный расклад', desc: 'Вы · другой · общий путь', price: '129 ⭐' },
      { name: 'Карьерный расклад', desc: 'Ситуация · вызов · результат', price: '129 ⭐' },
      { name: 'Разблокировать перевёрнутые', desc: 'Разово, навсегда', price: '149 ⭐' },
      { name: 'Подкова (5 карт)', desc: 'Прошлое · сейчас · скрытое · совет · итог', price: '179 ⭐' },
      { name: 'Месячный прогноз', desc: '4 карты — по одной на неделю', price: '249 ⭐' },
      { name: 'Кельтский крест', desc: 'Полный расклад из 10 карт', price: '299 ⭐' },
      { name: 'Колесо года', desc: '12 карт — по одной на месяц', price: '599 ⭐' },
    ],
    subsHeader: 'Подписки',
    subPro: { name: 'Pro', desc: 'Без лимитов: ежедневное, 3-карты, углубления, да/нет', price: '199 ⭐ /мес.' },
    subPremium: { name: 'Premium', desc: 'Всё Pro + кельтский крест + месячный прогноз', price: '499 ⭐ /мес.' },
    cta: 'Открыть в Telegram', secondary: 'Бот · @thisisthedailytarotbot',
    noSignup: 'Без регистрации · Без email · Без трекинга',
    langsLabel: 'Языки',
  },
  hi: {
    badge: 'Telegram में दैनिक टैरो', title: 'आज का आपका कार्ड',
    subtitle: 'Rider-Waite · 9 भाषाएँ · रनटाइम में AI नहीं · विज्ञापन नहीं',
    body: 'हर सुबह 9:00 बजे एक कार्ड। पूरे दिन वही कार्ड — निर्धारित, बदला नहीं जा सकता। नीचे — सेवाओं का पूरा कैटलॉग।',
    feature1: '78 Rider-Waite कार्ड · public domain',
    feature2: '9:00 पर दैनिक पुश · 9 भाषाएँ',
    feature3: 'Telegram Stars भुगतान · कोई अनिवार्य सदस्यता नहीं',
    feature4: 'अकाउंट नहीं, ईमेल नहीं, ट्रैकिंग नहीं',
    servicesHeader: 'आप क्या कर सकते हैं',
    freeRow: 'दैनिक कार्ड — मुफ़्त, हमेशा के लिए',
    services: [
      { name: 'अतिरिक्त कार्ड', desc: 'उसी दिन दूसरा क़दम', price: '30 ⭐' },
      { name: 'हाँ/ना प्रश्न', desc: 'एक कार्ड — स्पष्ट उत्तर', price: '39 ⭐' },
      { name: 'आज का कार्ड गहराई से', desc: 'प्रेम · करियर · पैसा · सलाह', price: '69 ⭐' },
      { name: '3-कार्ड स्प्रेड', desc: 'अतीत · वर्तमान · भविष्य', price: '99 ⭐' },
      { name: 'प्रेम स्प्रेड', desc: 'आप · वह · साझा मार्ग', price: '129 ⭐' },
      { name: 'करियर स्प्रेड', desc: 'स्थिति · चुनौती · परिणाम', price: '129 ⭐' },
      { name: 'उल्टे अर्थ अनलॉक', desc: 'एक बार, हमेशा के लिए', price: '149 ⭐' },
      { name: 'हॉर्सशू (5 कार्ड)', desc: 'अतीत · अभी · छिपा · सलाह · परिणाम', price: '179 ⭐' },
      { name: 'मासिक पूर्वानुमान', desc: '4 कार्ड — हर हफ़्ते एक', price: '249 ⭐' },
      { name: 'सेल्टिक क्रॉस', desc: 'पूर्ण 10-कार्ड स्प्रेड', price: '299 ⭐' },
      { name: 'वर्ष का चक्र', desc: '12 कार्ड — हर महीने एक', price: '599 ⭐' },
    ],
    subsHeader: 'सदस्यताएँ',
    subPro: { name: 'Pro', desc: 'असीमित: दैनिक, 3-कार्ड, गहराई, हाँ/ना', price: '199 ⭐ /माह' },
    subPremium: { name: 'Premium', desc: 'सब कुछ Pro + सेल्टिक क्रॉस + मासिक पूर्वानुमान', price: '499 ⭐ /माह' },
    cta: 'Telegram में खोलें', secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'रजिस्ट्रेशन नहीं · ईमेल नहीं · ट्रैकिंग नहीं',
    langsLabel: 'भाषाएँ',
  },
  pt: {
    badge: 'Tarô diário dentro do Telegram', title: 'Sua carta do dia',
    subtitle: 'Rider-Waite. 9 idiomas. Sem IA em runtime, sem anúncios.',
    body: 'Toda manhã às 9:00 você recebe uma carta. A mesma carta o dia inteiro — determinística, sem re-tiradas. Abaixo — o catálogo completo de serviços.',
    feature1: '78 cartas Rider-Waite · domínio público',
    feature2: 'Envio diário às 9:00 · 9 idiomas',
    feature3: 'Pagamentos Telegram Stars · sem assinatura forçada',
    feature4: 'Sem conta, sem e-mail, sem rastreamento',
    servicesHeader: 'O que você pode fazer',
    freeRow: 'Carta diária — grátis, para sempre',
    services: [
      { name: 'Carta extra', desc: 'Segundo movimento no mesmo dia', price: '30 ⭐' },
      { name: 'Pergunta sim/não', desc: 'Uma carta — resposta clara', price: '39 ⭐' },
      { name: 'Aprofundar a carta de hoje', desc: 'Amor · trabalho · dinheiro · conselho', price: '69 ⭐' },
      { name: 'Jogo de 3 cartas', desc: 'Passado · presente · futuro', price: '99 ⭐' },
      { name: 'Jogo do amor', desc: 'Você · a outra pessoa · caminho comum', price: '129 ⭐' },
      { name: 'Jogo do trabalho', desc: 'Situação · desafio · resultado', price: '129 ⭐' },
      { name: 'Desbloquear invertidas', desc: 'Uma vez, para sempre', price: '149 ⭐' },
      { name: 'Ferradura (5 cartas)', desc: 'Passado · agora · oculto · conselho · resultado', price: '179 ⭐' },
      { name: 'Previsão mensal', desc: '4 cartas — uma por semana', price: '249 ⭐' },
      { name: 'Cruz celta', desc: 'Jogo completo de 10 cartas', price: '299 ⭐' },
      { name: 'Roda do ano', desc: '12 cartas — uma por mês', price: '599 ⭐' },
    ],
    subsHeader: 'Assinaturas',
    subPro: { name: 'Pro', desc: 'Sem limites: diária, 3-cartas, aprofundamentos, sim/não', price: '199 ⭐ /mês' },
    subPremium: { name: 'Premium', desc: 'Tudo Pro + cruz celta + previsão mensal', price: '499 ⭐ /mês' },
    cta: 'Abrir no Telegram', secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'Sem cadastro · Sem email · Sem rastreamento',
    langsLabel: 'Idiomas',
  },
  es: {
    badge: 'Tarot diario dentro de Telegram', title: 'Tu carta del día',
    subtitle: 'Rider-Waite. 9 idiomas. Sin IA en runtime, sin anuncios.',
    body: 'Cada mañana a las 9:00 recibes una carta. La misma carta todo el día — determinista, sin volver a tirar. Abajo — el catálogo completo de servicios.',
    feature1: '78 cartas Rider-Waite · dominio público',
    feature2: 'Envío diario a las 9:00 · 9 idiomas',
    feature3: 'Pagos con Telegram Stars · sin suscripción obligatoria',
    feature4: 'Sin cuenta, sin email, sin rastreo',
    servicesHeader: 'Lo que puedes hacer',
    freeRow: 'Carta diaria — gratis, para siempre',
    services: [
      { name: 'Carta extra', desc: 'Segundo movimiento del mismo día', price: '30 ⭐' },
      { name: 'Pregunta sí/no', desc: 'Una carta — respuesta clara', price: '39 ⭐' },
      { name: 'Profundizar la carta de hoy', desc: 'Amor · trabajo · dinero · consejo', price: '69 ⭐' },
      { name: 'Tirada de 3 cartas', desc: 'Pasado · presente · futuro', price: '99 ⭐' },
      { name: 'Tirada del amor', desc: 'Tú · la otra persona · camino común', price: '129 ⭐' },
      { name: 'Tirada del trabajo', desc: 'Situación · desafío · resultado', price: '129 ⭐' },
      { name: 'Desbloquear invertidas', desc: 'Una vez, para siempre', price: '149 ⭐' },
      { name: 'Herradura (5 cartas)', desc: 'Pasado · ahora · oculto · consejo · resultado', price: '179 ⭐' },
      { name: 'Pronóstico mensual', desc: '4 cartas — una por semana', price: '249 ⭐' },
      { name: 'Cruz celta', desc: 'Tirada completa de 10 cartas', price: '299 ⭐' },
      { name: 'Rueda del año', desc: '12 cartas — una por mes', price: '599 ⭐' },
    ],
    subsHeader: 'Suscripciones',
    subPro: { name: 'Pro', desc: 'Sin límites: diaria, 3-cartas, profundizaciones, sí/no', price: '199 ⭐ /mes' },
    subPremium: { name: 'Premium', desc: 'Todo Pro + cruz celta + pronóstico mensual', price: '499 ⭐ /mes' },
    cta: 'Abrir en Telegram', secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'Sin registro · Sin email · Sin rastreo',
    langsLabel: 'Idiomas',
  },
  de: {
    badge: 'Tägliches Tarot in Telegram', title: 'Deine Karte des Tages',
    subtitle: 'Rider-Waite. 9 Sprachen. Keine Runtime-AI, keine Werbung.',
    body: 'Jeden Morgen um 9:00 bekommst du eine Karte. Den ganzen Tag dieselbe — deterministisch, kein Neumischen. Darunter — der komplette Service-Katalog.',
    feature1: '78 Rider-Waite-Karten · gemeinfrei',
    feature2: 'Tägliche Nachricht um 9:00 · 9 Sprachen',
    feature3: 'Zahlung mit Telegram Stars · kein Zwangsabo',
    feature4: 'Kein Konto, keine E-Mail, kein Tracking',
    servicesHeader: 'Was du tun kannst',
    freeRow: 'Tageskarte — kostenlos, für immer',
    services: [
      { name: 'Extra-Karte', desc: 'Zweiter Zug am selben Tag', price: '30 ⭐' },
      { name: 'Ja/Nein-Frage', desc: 'Eine Karte — klare Antwort', price: '39 ⭐' },
      { name: 'Heutige Karte vertiefen', desc: 'Liebe · Beruf · Geld · Rat', price: '69 ⭐' },
      { name: '3-Karten-Legung', desc: 'Vergangenheit · Gegenwart · Zukunft', price: '99 ⭐' },
      { name: 'Liebeslegung', desc: 'Du · die andere Person · gemeinsamer Weg', price: '129 ⭐' },
      { name: 'Karrierelegung', desc: 'Situation · Herausforderung · Ergebnis', price: '129 ⭐' },
      { name: 'Umgekehrte Bedeutungen', desc: 'Einmalig, für immer', price: '149 ⭐' },
      { name: 'Hufeisen (5 Karten)', desc: 'Vergangenheit · jetzt · Verborgenes · Rat · Ergebnis', price: '179 ⭐' },
      { name: 'Monatsprognose', desc: '4 Karten — eine pro Woche', price: '249 ⭐' },
      { name: 'Keltisches Kreuz', desc: 'Vollständige 10-Karten-Legung', price: '299 ⭐' },
      { name: 'Jahresrad', desc: '12 Karten — eine pro Monat', price: '599 ⭐' },
    ],
    subsHeader: 'Abonnements',
    subPro: { name: 'Pro', desc: 'Unbegrenzt: täglich, 3-Karten, Vertiefungen, Ja/Nein', price: '199 ⭐ /Mon.' },
    subPremium: { name: 'Premium', desc: 'Alles Pro + keltisches Kreuz + Monatsprognose', price: '499 ⭐ /Mon.' },
    cta: 'In Telegram öffnen', secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'Keine Registrierung · Keine E-Mail · Kein Tracking',
    langsLabel: 'Sprachen',
  },
  fr: {
    badge: 'Tarot quotidien dans Telegram', title: 'Ta carte du jour',
    subtitle: 'Rider-Waite. 9 langues. Pas d\'IA en runtime, pas de publicité.',
    body: 'Chaque matin à 9:00, tu reçois une carte. La même carte toute la journée — déterministe, pas de re-tirage. Dessous — le catalogue complet des services.',
    feature1: '78 cartes Rider-Waite · domaine public',
    feature2: 'Envoi quotidien à 9:00 · 9 langues',
    feature3: 'Paiements Telegram Stars · sans abonnement forcé',
    feature4: 'Pas de compte, pas d\'email, pas de tracking',
    servicesHeader: 'Ce que tu peux faire',
    freeRow: 'Carte quotidienne — gratuite, pour toujours',
    services: [
      { name: 'Carte supplémentaire', desc: 'Second mouvement du même jour', price: '30 ⭐' },
      { name: 'Question oui/non', desc: 'Une carte — réponse claire', price: '39 ⭐' },
      { name: 'Approfondir la carte du jour', desc: 'Amour · travail · argent · conseil', price: '69 ⭐' },
      { name: 'Tirage 3 cartes', desc: 'Passé · présent · futur', price: '99 ⭐' },
      { name: 'Tirage d\'amour', desc: 'Toi · l\'autre · le chemin commun', price: '129 ⭐' },
      { name: 'Tirage carrière', desc: 'Situation · défi · résultat', price: '129 ⭐' },
      { name: 'Sens inversés', desc: 'Une fois, pour toujours', price: '149 ⭐' },
      { name: 'Fer à cheval (5 cartes)', desc: 'Passé · maintenant · caché · conseil · résultat', price: '179 ⭐' },
      { name: 'Prévision mensuelle', desc: '4 cartes — une par semaine', price: '249 ⭐' },
      { name: 'Croix celtique', desc: 'Tirage complet de 10 cartes', price: '299 ⭐' },
      { name: 'Roue de l\'année', desc: '12 cartes — une par mois', price: '599 ⭐' },
    ],
    subsHeader: 'Abonnements',
    subPro: { name: 'Pro', desc: 'Sans limite : quotidien, 3-cartes, approfondissements, oui/non', price: '199 ⭐ /mois' },
    subPremium: { name: 'Premium', desc: 'Tout Pro + croix celtique + prévision mensuelle', price: '499 ⭐ /mois' },
    cta: 'Ouvrir dans Telegram', secondary: 'Bot · @thisisthedailytarotbot',
    noSignup: 'Sans inscription · Sans email · Sans tracking',
    langsLabel: 'Langues',
  },
};

const HERO_CARDS = ['19.jpg', '17.jpg', '21.jpg', '00.jpg', '01.jpg'];
const ALL_LANGS = '🇵🇱 PL · 🇬🇧 EN · 🇺🇦 UK · 🇷🇺 RU · 🇮🇳 HI · 🇧🇷 PT · 🇪🇸 ES · 🇩🇪 DE · 🇫🇷 FR';

interface Props { lang: string; botUsername: string }

export function Landing({ lang, botUsername }: Props) {
  const c = COPY[lang] ?? COPY.en!;
  const heroCard = HERO_CARDS[Math.floor(Math.random() * HERO_CARDS.length)]!;
  const botLink = `https://t.me/${botUsername}`;

  return (
    <div className="landing">
      <section className="landing-hero">
        <div className="landing-hero-copy">
          <div className="landing-badge">{c.badge}</div>
          <h1 className="landing-title">{c.title}</h1>
          <p className="landing-subtitle">{c.subtitle}</p>
          <p className="landing-body">{c.body}</p>
          <a className="landing-cta" href={botLink}>{c.cta}</a>
          <div className="landing-secondary"><a href={botLink}>{c.secondary}</a></div>
        </div>
        <div className="landing-hero-visual">
          <img src={`/cards/${heroCard}`} alt="" draggable={false} />
        </div>
      </section>

      <section className="landing-features">
        <div className="landing-feature">✦ {c.feature1}</div>
        <div className="landing-feature">✦ {c.feature2}</div>
        <div className="landing-feature">✦ {c.feature3}</div>
        <div className="landing-feature">✦ {c.feature4}</div>
      </section>

      <section className="landing-services">
        <h2>{c.servicesHeader}</h2>
        <div className="landing-free">★ {c.freeRow}</div>
        <ul className="landing-services-list">
          {c.services.map((s, i) => (
            <li key={i}>
              <span className="landing-svc-name">{s.name}</span>
              <span className="landing-svc-desc">{s.desc}</span>
              <span className="landing-svc-price">{s.price}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="landing-subs">
        <h2>{c.subsHeader}</h2>
        <ul className="landing-services-list">
          <li>
            <span className="landing-svc-name">{c.subPro.name}</span>
            <span className="landing-svc-desc">{c.subPro.desc}</span>
            <span className="landing-svc-price">{c.subPro.price}</span>
          </li>
          <li>
            <span className="landing-svc-name">{c.subPremium.name}</span>
            <span className="landing-svc-desc">{c.subPremium.desc}</span>
            <span className="landing-svc-price">{c.subPremium.price}</span>
          </li>
        </ul>
      </section>

      <section className="landing-langs">
        <div className="landing-langs-label">{c.langsLabel}</div>
        <div className="landing-langs-flags">{ALL_LANGS}</div>
      </section>

      <section className="landing-footer">
        <a className="landing-cta landing-cta-small" href={botLink}>{c.cta}</a>
        <div className="landing-fine-print">{c.noSignup}</div>
      </section>
    </div>
  );
}
